import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refs, type CashFlowItemRef } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * ч.732: статті руху грошових коштів (ДДС) у кабінеті клієнта.
 * Системні статті веде платформа (read-only, 🔒). Власні — створює клієнт
 * або створює імпорт з 1С (за кодом 1С). Власну статтю можна перейменувати,
 * змінити вид діяльності, обʼєднати з іншою (усі документи переходять на
 * цільову — так «Оплата поставщику» з 1С стає системною «Оплата постачальникам»).
 */
const FLOW_LABEL: Record<CashFlowItemRef['flow_type'], string> = {
  OPERATING: 'Операційна', INVESTING: 'Інвестиційна', FINANCING: 'Фінансова',
};
type Flow = CashFlowItemRef['flow_type'];

export default function CashFlowItemsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [newFlow, setNewFlow] = useState<Flow>('OPERATING');
  const [mergeFor, setMergeFor] = useState<CashFlowItemRef | null>(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['cash-flow-items'], queryFn: refs.cashFlowItems });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['cash-flow-items'] });
  const errText = (e: unknown, fb: string) =>
    (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fb;

  const create = useMutation({
    mutationFn: () => refs.cashFlowItemsOwn.create({ name: newName.trim(), flow_type: newFlow }),
    onSuccess: () => { setNewName(''); setNote(null); invalidate(); },
    onError: (e) => setNote(errText(e, 'Не вдалося створити статтю.')),
  });
  const update = useMutation({
    mutationFn: ({ id, p }: { id: string; p: { name?: string; flow_type?: Flow } }) =>
      refs.cashFlowItemsOwn.update(id, p),
    onSuccess: () => { setNote(null); invalidate(); },
    onError: (e) => setNote(errText(e, 'Не вдалося оновити статтю.')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.cashFlowItemsOwn.remove(id),
    onSuccess: () => { setNote(null); invalidate(); },
    onError: (e) => setNote(errText(e, 'Не вдалося видалити статтю.')),
  });
  const merge = useMutation({
    mutationFn: ({ id, target }: { id: string; target: string }) => refs.cashFlowItemsOwn.merge(id, target),
    onSuccess: (res) => {
      setNote(`Обʼєднано: перенесено посилань — ${res.total}.`);
      setMergeFor(null); setMergeTarget(''); invalidate();
    },
    onError: (e) => setNote(errText(e, 'Не вдалося обʼєднати.')),
  });

  const rows = list.data ?? [];
  const system = rows.filter((r) => r.is_system);
  const own = rows.filter((r) => !r.is_system);

  const sysCols = useMemo<ColumnDef<CashFlowItemRef>[]>(() => [
    { key: 'flow_type', header: 'Діяльність', width: 130, sortValue: (r) => r.flow_type, cell: (r) => FLOW_LABEL[r.flow_type] },
    { key: 'code', header: 'Код', width: 150, sortValue: (r) => r.code, cell: (r) => <span style={{ fontFamily: 'monospace' }}>{r.code}</span> },
    { key: 'name', header: 'Назва', sortValue: (r) => r.name.toLowerCase(), cell: (r) => <>🔒 {r.name}</> },
    { key: 'usage_count', header: 'Документів', width: 110, align: 'right', sortValue: (r) => r.usage_count ?? 0, cell: (r) => r.usage_count ?? 0 },
  ], []);

  const ownCols = useMemo<ColumnDef<CashFlowItemRef>[]>(() => [
    {
      key: 'flow_type', header: 'Діяльність', width: 140, sortValue: (r) => r.flow_type,
      cell: (r) => (
        <select value={r.flow_type} onChange={(e) => update.mutate({ id: r.id, p: { flow_type: e.target.value as Flow } })}>
          {(Object.keys(FLOW_LABEL) as Flow[]).map((f) => <option key={f} value={f}>{FLOW_LABEL[f]}</option>)}
        </select>
      ),
    },
    { key: 'code', header: 'Код (з 1С)', width: 130, sortValue: (r) => r.code, cell: (r) => <span style={{ fontFamily: 'monospace' }}>{r.code}</span> },
    {
      key: 'name', header: 'Назва', sortValue: (r) => r.name.toLowerCase(),
      cell: (r) => (
        <input defaultValue={r.name} style={{ width: '95%' }}
          onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== r.name) update.mutate({ id: r.id, p: { name: v } }); }} />
      ),
    },
    { key: 'usage_count', header: 'Документів', width: 110, align: 'right', sortValue: (r) => r.usage_count ?? 0, cell: (r) => r.usage_count ?? 0 },
    {
      key: 'actions', header: '', width: 190, align: 'center',
      cell: (r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <button type="button" title="Перенести всі документи на іншу статтю і прибрати цю"
            onClick={() => { setMergeFor(r); setMergeTarget(''); }}>обʼєднати</button>{' '}
          <button type="button" className="danger" title="Видалити (лише якщо не використовується)"
            onClick={() => confirm(`Видалити статтю «${r.name}»?`).then((ok) => { if (ok) remove.mutate(r.id); })}>✕</button>
        </span>
      ),
    },
  ], [update, remove, confirm]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Статті руху грошових коштів (ДДС)</h2>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 820 }}>
        Системні статті (🔒) спільні для всіх і ведуться платформою. Власні статті
        створюєте ви або імпорт з 1С (за кодом 1С). Власну можна перейменувати,
        змінити вид діяльності або <b>обʼєднати</b> з іншою — усі документи з нею
        перейдуть на цільову статтю (так дублікати з 1С зводяться до системних).
      </p>
      {note && (
        <div style={{ margin: '0.5rem 0', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.85rem',
          background: note.startsWith('Не вдалося') ? '#fee2e2' : '#dcfce7',
          border: `1px solid ${note.startsWith('Не вдалося') ? '#fca5a5' : '#86efac'}` }}>
          {note}
          <button type="button" style={{ marginLeft: 8, padding: '0 0.4rem' }} onClick={() => setNote(null)}>×</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.75rem 0' }}>
        <input placeholder="Назва нової власної статті" value={newName}
          onChange={(e) => setNewName(e.target.value)} style={{ minWidth: 300 }} />
        <select value={newFlow} onChange={(e) => setNewFlow(e.target.value as Flow)} style={{ width: 160 }}>
          {(Object.keys(FLOW_LABEL) as Flow[]).map((f) => <option key={f} value={f}>{FLOW_LABEL[f]}</option>)}
        </select>
        <button className="primary" disabled={!newName.trim() || create.isPending} onClick={() => create.mutate()}>
          + Додати статтю
        </button>
      </div>

      <h3 style={{ margin: '0.75rem 0 0.4rem' }}>Власні статті ({own.length})</h3>
      <DataTable<CashFlowItemRef> rows={own} columns={ownCols} rowKey={(r) => r.id}
        loading={list.isLoading} emptyText="Власних статей немає — використовуються системні." initialSortKey="code" />

      <h3 style={{ margin: '1rem 0 0.4rem' }}>Системні статті ({system.length})</h3>
      <DataTable<CashFlowItemRef> rows={system} columns={sysCols} rowKey={(r) => r.id}
        loading={list.isLoading} emptyText="Системних статей ще немає." initialSortKey="flow_type" />

      {mergeFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setMergeFor(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 10, padding: '1rem', width: 560 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Обʼєднати «{mergeFor.name}» з іншою статтею</h3>
            <p style={{ color: 'var(--muted)', fontSize: '0.88rem' }}>
              Усі документи ({mergeFor.usage_count ?? 0}), договори та правила розпізнавання з цією статтею
              перейдуть на обрану, а «{mergeFor.name}» буде прибрана з довідника.
            </p>
            <select value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} style={{ width: '100%' }}>
              <option value="">— оберіть цільову статтю —</option>
              {(['OPERATING', 'INVESTING', 'FINANCING'] as Flow[]).map((g) => {
                const items = rows.filter((x) => x.flow_type === g && x.id !== mergeFor.id);
                if (!items.length) return null;
                return (
                  <optgroup key={g} label={FLOW_LABEL[g]}>
                    {items.map((x) => <option key={x.id} value={x.id}>{x.is_system ? '🔒 ' : ''}{x.name}</option>)}
                  </optgroup>
                );
              })}
            </select>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
              <button type="button" onClick={() => setMergeFor(null)}>скасувати</button>
              <button type="button" className="primary" disabled={!mergeTarget || merge.isPending}
                onClick={() => merge.mutate({ id: mergeFor.id, target: mergeTarget })}>
                {merge.isPending ? 'обʼєднуємо…' : 'обʼєднати'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
